import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
    Cart,
    CartException,
    CartFees,
    PayloadPayPal,
    PaymentMethods,
    PaymentMethodsType,
    StationFees,
} from '@cargos/sprintpay-models';
import { combineLatest, map, Observable, of, Subject, switchMap, take, takeUntil } from 'rxjs';
import { CartBillService, CustomerService, PaymentCartService } from 'src/app/shared/services';
import { FeatureRestrictionsService } from 'src/app/shared/services/features/feature-restrictions.service';
import { PromoCodesService } from 'src/app/shared/services/promo-code.service';
import { PaymentMethodsSelected } from '../shared/models/payment-method.model';
import { PaymentMethodsHandlerService } from '../shared/services/payment-methods-handler.service';
import { AutomatedDebitsResponse, Fee } from './shared/models';

export enum EWRFees {
    PORT_FEE = 'Port Fee',
    EXAM_FEE = 'Hdlg&Mnfst Fee',
}

@Component({
    selector: 'app-lib-balance',
    templateUrl: './balance.component.html',
    styleUrls: ['./balance.component.scss'],
    standalone: false,
})
export class BalanceComponent implements OnInit, OnDestroy {
    public paymentMethodBelowThreshold: PaymentMethodsType | null = null;
    public paymentMethodAboveThreshold: PaymentMethodsType | null = null;
    public isCartAboveThreshold: boolean = false;
    public subTotal: number = 0;
    public lendingTerms: number = 0;
    public cartBill: Cart | null = null;
    public isInvoiceCheckLimitValid: boolean = false;
    public invoicesInCart: boolean = false;
    public nonReservationFee: number = 0;
    public lendingFeeTotal: number = 0;
    public lendingRate: number = 0;
    public readonly paymentMethods = PaymentMethods;
    public isCreditCardFeeAvailable: boolean = false;
    private unsubscribe$: Subject<void> = new Subject<void>();
    public isPaymentMethodSelectedValid?: boolean;
    public isAutomatedDebitsEnabled: boolean = false;
    public sprintPayFlexEnabled: boolean = false;
    public sprintPayFlexButtonText: string = 'Finance This Payment';
    public sprintPayFlexButtonDisabled: boolean = false;
    public financeDisclaimer: string = '';
    public cartFees = CartFees;
    public chargeAndFeesSubTotal = 0;
    public shouldShowConvenienceFeesDivider = false;
    public paymentMethodNameSelected: string;
    public cartSubTotal: number = 0;
    public paymentSubmitted: boolean = false;
    public isFinanceEnabled: boolean = false;
    public taxAmountTotal: number | undefined = undefined;
    public hasConvenienceFeesWithValue: boolean = false;

    // SPF
    public SPFUnderLimitRate: number = 0;
    public SPFOverLimitRate: number = 0;
    public SPFUnderLimitSubTotal: number = 0;
    public SPFOverLimitSubTotal: number = 0;
    public SPFUnderLimitFee: number = 0;
    public SPFOverLimitFee: number = 0;

    @Input() termsOfUseURL: string = '';
    @Input() convenienceFees: Fee[] = [];
    @Input() titleCustomFees: string = '';
    @Input() customSubtotal: boolean = false;
    @Input() customSubtotalTitle: string = '';
    @Input() cartNote: string = '';
    @Input() cartNoteWithCheckbox: boolean = false;
    @Input() isAllowedMultipleAWBs: boolean = false;
    @Output() payPaypal: EventEmitter<PayloadPayPal> = new EventEmitter();

    constructor(
        private matDialog: MatDialog,
        private changeDetectorRef: ChangeDetectorRef,
        private promoCodeService: PromoCodesService,
        private featureRestrictionsService: FeatureRestrictionsService,
        private paymentMethodsHandlerService: PaymentMethodsHandlerService,
        private customerService: CustomerService,
        private cartBillService: CartBillService,
        private paymentCartService: PaymentCartService
    ) {}

    ngOnInit(): void {
        this.subscribeGetCurrentCart();
        this.subscribeToInvoiceCheckLimit();
        this.subcribePaymentMethodsSelectedBasedOnThreshold();
        this.isPaymentMethodValid();
        this.isAutomatedDebitsEnable();
        this.handlesprintPayFlexStatus();
        this.subscribeSPFRate();
        this.subscribeSubTotal();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
        this.matDialog.closeAll();
        this.changeDetectorRef.detach();
    }

    /**
     * @method subscribeGetCurrentCart()
     * @description Subscribes to the current cart and updates the cart bill and subtotal.
     *              This method listens for changes in the cart bill and updates the component state accordingly.
     *              It also checks for invoice limits and sets the invoice limit flag if necessary.
     */
    private subscribeGetCurrentCart(): void {
        this.cartBillService
            .getCartBill$()
            .pipe(
                switchMap((cartBill: Cart | null) => {
                    return this.promoCodeService.getNonReservationFee$().pipe(
                        take(1),
                        map((nonReservationFeeTotal) => {
                            const isThereInvoicesInCart = this.cartBillService.isThereInvoicesInCart();
                            const isTherePaymentsRequestsNoInvoices = this.cartBillService.isTherePaymentsRequests();
                            return {
                                cartBill,
                                isThereInvoicesInCart,
                                isTherePaymentsRequestsNoInvoices,
                                nonReservationFeeTotal,
                            };
                        })
                    );
                }),
                map(
                    ({
                        cartBill,
                        isThereInvoicesInCart,
                        isTherePaymentsRequestsNoInvoices,
                        nonReservationFeeTotal,
                    }) => {
                        let convenienceFeesAndSubtotal = cartBill?.subTotal || 0;
                        const subTotal = (cartBill?.subTotal || 0) + (cartBill?.threshold?.subTotal || 0);
                        let feesSubTotal = 0;
                        let total = 0;
                        this.convenienceFees.forEach((feesConfiguration) => {
                            const key = feesConfiguration.feeName as keyof Cart;
                            feesConfiguration.feeValue = Number(
                                Number(cartBill?.[key] || 0) +
                                    Number(
                                        cartBill?.threshold?.[
                                            feesConfiguration.feeName as keyof typeof cartBill.threshold
                                        ] || 0
                                    )
                            );
                            if (
                                !feesConfiguration.extraFee &&
                                feesConfiguration.feeName !== CartFees.NON_RESERVATION_FEE &&
                                feesConfiguration.feeName !== CartFees.STATION_FEES
                            ) {
                                feesSubTotal =
                                    feesSubTotal +
                                    Number(cartBill?.[key] || 0) +
                                    Number(cartBill?.threshold?.[key] || 0);
                            }

                            if (feesConfiguration.feeName === CartFees.NON_RESERVATION_FEE) {
                                feesSubTotal = feesSubTotal + nonReservationFeeTotal;

                                feesConfiguration.feeValue = nonReservationFeeTotal;
                            }

                            if (feesConfiguration.feeName === 'customsFee') {
                                feesConfiguration.feeLabel = cartBill?.customsFeeLabel || '';
                            }

                            if (feesConfiguration.feeName === CartFees.STATION_FEES) {
                                const stationFees = this.getStationFees(
                                    cartBill?.stationFees || [],
                                    cartBill?.threshold?.stationFees || []
                                );
                                stationFees.forEach((fee) => {
                                    this.convenienceFees.push({
                                        feeLabel: fee.label,
                                        feeValue: fee.amount,
                                        feeName: fee.label,
                                        getFeeInfoTooltip: feesConfiguration.getFeeInfoTooltip,
                                    });

                                    feesSubTotal = feesSubTotal + Number(fee.amount || 0);
                                });
                            }
                        });
                        convenienceFeesAndSubtotal = feesSubTotal + Number(subTotal || 0);

                        if (cartBill?.total === 0) {
                            total = convenienceFeesAndSubtotal;
                        } else {
                            total = (cartBill?.subTotal || 0) + (cartBill?.threshold?.subTotal || 0);
                        }
                        return {
                            cartBill,
                            isThereInvoicesInCart,
                            isTherePaymentsRequestsNoInvoices,
                            nonReservationFeeTotal,
                            convenienceFeesTotal: convenienceFeesAndSubtotal,
                            newTotal: total,
                        };
                    }
                ),
                takeUntil(this.unsubscribe$)
            )
            .subscribe({
                next: ({ cartBill, isThereInvoicesInCart, nonReservationFeeTotal, convenienceFeesTotal, newTotal }) => {
                    this.invoicesInCart = isThereInvoicesInCart;
                    this.cartBill = cartBill;
                    this.subTotal = (cartBill?.subTotal || 0) + (cartBill?.threshold?.subTotal || 0);
                    this.nonReservationFee = nonReservationFeeTotal || 0;
                    this.lendingTerms = cartBill?.lendingTerms || cartBill?.threshold?.lendingTerms || 0;
                    this.lendingFeeTotal =
                        (cartBill?.lendingInterest || 0) + (cartBill?.threshold?.lendingInterest || 0);
                    this.lendingRate = cartBill?.lendingRate || cartBill?.threshold?.lendingRate || 0;
                    this.cartSubTotal = newTotal;
                    this.chargeAndFeesSubTotal = convenienceFeesTotal;
                    this.SPFUnderLimitFee = cartBill?.lendingInterest || 0;
                    this.SPFOverLimitFee = cartBill?.threshold?.lendingInterest || 0;
                    this.taxAmountTotal = cartBill?.taxAmountTotal;
                    this.updateFinanceDisclaimer();
                    this.hasConvenienceFeesWithValue = this.convenienceFees.some(
                        (fee) => !fee.extraFee && fee.feeValue > 0
                    );
                },
            });
    }

    public subscribeSPFRate(): void {
        this.featureRestrictionsService
            .isPaymentMethodValid$()
            .pipe(
                takeUntil(this.unsubscribe$),
                switchMap((isPaymentMethodSelectedValid) => {
                    this.isPaymentMethodSelectedValid = !!isPaymentMethodSelectedValid;
                    if (!isPaymentMethodSelectedValid) {
                        return of([0, 0]);
                    }
                    return combineLatest([
                        this.cartBillService.getSPFRateByThreshold(false),
                        this.cartBillService.getSPFRateByThreshold(true),
                    ]);
                })
            )
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: ([underLimitRate, overLimitRate]) => {
                    this.SPFUnderLimitRate = underLimitRate || 0;
                    this.SPFOverLimitRate = overLimitRate || 0;
                },
            });
    }

    getStationFees(stationFees: StationFees[], thresholdStationFees: StationFees[]): StationFees[] {
        const feeMap = new Map<string, number>();

        const combinedArray = [...stationFees, ...thresholdStationFees];
        for (const fee of combinedArray) {
            feeMap.set(fee.label, (feeMap.get(fee.label) || 0) + fee.amount);
        }

        return Array.from(feeMap, ([label, amount]) => ({ label, amount }));
    }

    subscribeToInvoiceCheckLimit(): void {
        this.featureRestrictionsService
            .isInvoiceCheckLimitValid$()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (isInvoiceCheckLimitValid) => {
                    this.isInvoiceCheckLimitValid = isInvoiceCheckLimitValid;
                },
            });
    }

    subcribePaymentMethodsSelectedBasedOnThreshold(): void {
        this.getPaymentMethodsSelectedBasedOnThreshold()
            .pipe(
                map((paymentMethodsSelected: PaymentMethodsSelected) => {
                    return {
                        paymentMethodsSelected,
                        isCreditCardFeeAvailable:
                            this.isPaymentMethodSelected(paymentMethodsSelected, PaymentMethods.CREDIT_CARD) ||
                            this.isPaymentMethodSelected(paymentMethodsSelected, PaymentMethods.PAYPAL),
                    };
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe({
                next: ({ paymentMethodsSelected, isCreditCardFeeAvailable }) => {
                    this.isCreditCardFeeAvailable = isCreditCardFeeAvailable;
                    this.paymentMethodBelowThreshold = paymentMethodsSelected.paymentMethodSelected || null;
                    this.paymentMethodAboveThreshold =
                        paymentMethodsSelected.paymentMethodSelectedAboveThreshold || null;
                    this.isCartAboveThreshold = this.cartBillService.isCartAboveThreshold();
                },
            });
    }

    getPaymentMethodsSelectedBasedOnThreshold(): Observable<PaymentMethodsSelected> {
        return combineLatest([
            this.cartBillService.isCartAboveThreshold$(),
            this.cartBillService.isCartBelowThreshold$(),
        ]).pipe(
            switchMap(([isCartAboveThreshold, isCartBelowThreshold]) => {
                return this.paymentMethodsHandlerService.getPaymentMethodsSelectedBasedOnThreshold$(
                    isCartAboveThreshold,
                    isCartBelowThreshold
                );
            })
        );
    }

    private isPaymentMethodSelected(
        paymentMethodsSelected: PaymentMethodsSelected,
        paymentMethodSelected: PaymentMethodsType
    ): boolean {
        return (
            paymentMethodsSelected.paymentMethodSelected === paymentMethodSelected ||
            paymentMethodsSelected.paymentMethodSelectedAboveThreshold === paymentMethodSelected
        );
    }

    isPaymentMethodValid(): void {
        this.featureRestrictionsService
            .isPaymentMethodValid$()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (isPaymentMethodSelectedValid) => {
                    this.isPaymentMethodSelectedValid = isPaymentMethodSelectedValid;
                },
            });
    }

    public isAutomatedDebitsEnable(): void {
        this.customerService
            .hasAutomatedDebits$()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (value: AutomatedDebitsResponse) => {
                    this.isAutomatedDebitsEnabled = !!value.customerAccountAutomatedDebit;
                    this.updateFinanceDisclaimer();
                },
            });
    }

    private handlesprintPayFlexStatus(): void {
        this.sprintPayFlexEnabled = this.customerService.isCTALendingEnabled();
    }

    public updateFinanceDisclaimer(): void {
        if (
            (![PaymentMethods.FINANCE].includes(this.paymentMethodBelowThreshold || '') &&
                ![PaymentMethods.FINANCE].includes(this.paymentMethodAboveThreshold || '')) ||
            !this.lendingTerms
        ) {
            this.financeDisclaimer = '';
            return;
        }
        this.financeDisclaimer = this.isAutomatedDebitsEnabled
            ? `Your bank account will be <span class="fw-bold"> debited  ${this.lendingTerms} days after </span> this Payment Request is processed.`
            : `Payment is due <span class="fw-bold"> in ${this.lendingTerms} days</span>.`;
    }

    public subscribeSubTotal(): void {
        combineLatest([
            this.cartBillService.getSubTotalByThreshold(false),
            this.cartBillService.getSubTotalByThreshold(true),
        ])
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: ([underLimitRate, overLimitRate]) => {
                    this.SPFUnderLimitSubTotal = underLimitRate || 0;
                    this.SPFOverLimitSubTotal = overLimitRate || 0;
                },
            });
    }

    updateShowConvenienceFeesDivider(): void {
        this.shouldShowConvenienceFeesDivider = this.convenienceFees.some((fee) => fee.extraFee && fee.feeValue > 0);
    }

    submitPayment(): void {
        this.paymentSubmitted = true;
        this.paymentCartService.setSubmitAccures(true);
    }

    payWithPaypal(event: PayloadPayPal): void {
        this.payPaypal.emit(event);
    }

    reviewAllErrors(event: CartException[]): void {
        // function to review all errors before payment submission
    }

    hasFinancePaymentMethod(): void {
        this.isFinanceEnabled =
            this.paymentMethodBelowThreshold === PaymentMethods.FINANCE ||
            this.paymentMethodAboveThreshold === PaymentMethods.FINANCE;
    }
}
